import { useEffect, useMemo } from "react";
import { listCommands } from "@/lib/commands/registry";
import {
  matchesAccelerator,
  parseAccelerator,
  type ParsedAccelerator,
} from "@/lib/commands/accelerator";
import { useRunCommand } from "@/lib/commands/CommandProvider";
import type { CommandDefinition } from "@/lib/commands/types";
import { csNoteClickFromEvent } from "@/lib/channel-switch-profile";

interface Binding {
  command: CommandDefinition;
  accelerator: ParsedAccelerator;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA") return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Wires the keyboard layer to the command registry. Parses every command's
 * accelerator once, listens for matching keydown events on `window`, and
 * dispatches through the runtime bridge installed by `CommandProvider`.
 *
 * Must be mounted exactly once inside a `CommandProvider`.
 */
export function useCommandShortcuts(): void {
  const run = useRunCommand();

  const bindings = useMemo<Binding[]>(() => {
    const result: Binding[] = [];
    for (const command of listCommands()) {
      if (!command.accelerator) continue;
      try {
        result.push({
          command,
          accelerator: parseAccelerator(command.accelerator),
        });
      } catch (err) {
        console.error(
          `[commands] Invalid accelerator on "${command.id}":`,
          command.accelerator,
          err,
        );
      }
    }
    return result;
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const typing = isTypingTarget(event.target);
      for (const binding of bindings) {
        if (!matchesAccelerator(event, binding.accelerator)) continue;
        if (typing && binding.command.scope === "outside-input") continue;
        event.preventDefault();
        // Annotate channel-switch commands with the keyboard event's
        // timestamp so csMark can compute the handler-queue delay
        // (how long the OS/browser sat on this keydown before
        // dispatching it to us — high values mean the main thread
        // was busy with a prior switch's render).
        if (
          binding.command.id === "channel.switchPrev" ||
          binding.command.id === "channel.switchNext"
        ) {
          csNoteClickFromEvent("kbd", event.timeStamp);
        }
        run(binding.command.id);
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [bindings, run]);
}
